import { useState, useMemo, useCallback } from "react";
import { useLoader } from "@/hooks/LoaderProvider";
import { PlusOutlined } from "@/config/antd";
import PageHeader from "@/components/layout/Header/Page_Header";
import Button from "@/components/common/Button";
import SearchFilterBar from "@/components/specific/Search_&_Filter";
import CustomPagination from "@/components/specific/Pagination";
import CandidateCard from "@/pages/candidates/components/Candidate_Card";
import EmptyState from "@/pages/candidates/components/Empty_State";
import CustomCollapse from "@/components/specific/Collapse";
import AddCandidateModal from "@/pages/candidates/components/Add_Candidate_Modal";
import { DUMMY_CANDIDATES } from "@/pages/candidates/dummyCandidates";

const Candidates = () => {
  const [searchValue, setSearchValue] = useState("");
  const [filterValue, setFilterValue] = useState("All Positions");
  const [activeFilter, setActiveFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;
  const { showLoader, hideLoader } = useLoader();
  const [isAddCandidateModalVisible, setIsAddCandidateModalVisible] =
    useState(false);
  const [localCandidates, setLocalCandidates] = useState([]);
  const [openCollapseSection, setOpenCollapseSection] =
    useState("Presidential");

  // Transform data (dummy + locally added) to match UI requirements.
  // `DUMMY_CANDIDATES` so you can develop the UI in isolation.
  const candidates = useMemo(() => {
    // Combine static dummy candidates with locally added ones
    const combinedCandidates = [...DUMMY_CANDIDATES, ...localCandidates];

    return combinedCandidates.map((candidate) => ({
      id: candidate._id || candidate.id,
      name: candidate.fullName || candidate.name,
      position: candidate.position,
      location: candidate.state || "Countrywide",
      avatar: candidate.avatar || null,
      party: candidate.party,
      organizationID: candidate.organizationID,
      createdAt: candidate.createdAt || new Date().toISOString(),
      updatedAt: candidate.updatedAt || new Date().toISOString(),
      // Additional UI-specific data
      runningMate: candidate.runningMate || {
        name: "Running Mate",
        avatar: null,
      },
      stats: candidate.stats || {
        voters: Math.floor(Math.random() * 500000),
        competitors: Math.floor(Math.random() * 10),
        winnings: Math.floor(Math.random() * 100),
        losses: Math.floor(Math.random() * 20),
      },
      registeredVoters:
        candidate.registeredVoters || Math.floor(Math.random() * 3000000),
      lastUpdated: candidate.updatedAt
        ? new Date(candidate.updatedAt).toLocaleString()
        : new Date().toLocaleString(),
    }));
  }, [localCandidates]);

  // Group candidates by position for the collapsible sections
  const groupedCandidates = useMemo(() => {
    const groups = {
      Presidential: [],
      Gubernatorial: [],
      Senatorial: [],
      "National Assembly": [],
      Chairmanship: [],
      Councillorship: [],
    };

    candidates.forEach((candidate) => {
      const position = candidate.position;
      if (position.toLowerCase().includes("president")) {
        groups.Presidential.push(candidate);
      } else if (position.toLowerCase().includes("governor")) {
        groups.Gubernatorial.push(candidate);
      } else if (position.toLowerCase().includes("senator")) {
        groups.Senatorial.push(candidate);
      } else if (position.toLowerCase().includes("assembly")) {
        groups["National Assembly"].push(candidate);
      } else if (position.toLowerCase().includes("chairman")) {
        groups.Chairmanship.push(candidate);
      } else if (position.toLowerCase().includes("councillor")) {
        groups.Councillorship.push(candidate);
      }
    });

    return groups;
  }, [candidates]);

  // Filter candidates based on the selected position filter
  const filteredCandidates = useMemo(() => {
    if (activeFilter === "all" || filterValue === "All Positions") {
      return candidates;
    }

    // Filter by position type
    return candidates.filter((candidate) => {
      const position = candidate.position.toLowerCase();

      switch (activeFilter) {
        case "presidential":
          return position.includes("president");
        case "gubernatorial":
          return position.includes("governor");
        case "senatorial":
          return position.includes("senator");
        case "assembly":
          return position.includes("assembly");
        case "chairmanship":
          return position.includes("chairman");
        case "councillorship":
          return position.includes("councillor");
        default:
          return true;
      }
    });
  }, [candidates, activeFilter, filterValue]);

  const handleSearch = (value) => {
    setSearchValue(value);
  };

  const handleFilterChange = (value) => {
    setActiveFilter(value);

    // Set display text for the filter button based on the selected filter
    const selectedFilter = filterOptions.items.find(
      (item) => item.key === value
    );
    if (selectedFilter) {
      setFilterValue(selectedFilter.label);
    }
  };

  const handleRefresh = useCallback(async () => {
    // 1. Reset pagination to first page
    setCurrentPage(1);

    // 2. Clear the search term
    setSearchValue("");

    // 3. Reset the active filter to 'all'
    setActiveFilter("all");

    // 4. Reset the filter value display text
    setFilterValue("All Positions");

    // 5. Reset the collapse section to default (Presidential)
    setOpenCollapseSection("Presidential");

    // 6. Reset local candidates and simulate a quick "refresh".
    // We keep this simple since we're on dummy data for now.
    showLoader();
    setLocalCandidates([]);
    hideLoader();
  }, [showLoader, hideLoader]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleSetupClick = () => {
    // Add setup navigation logic here
    showLoader();
    setTimeout(() => {
      hideLoader();
      setIsAddCandidateModalVisible(true);
      // navigate to setup page
    }, 2000);
  };

  // Configure position-based filter options (for mobile view)
  const filterOptions = {
    items: [
      { key: "all", label: "All Positions" },
      { key: "presidential", label: "Presidential" },
      { key: "gubernatorial", label: "Gubernatorial" },
      { key: "senatorial", label: "Senatorial" },
      { key: "assembly", label: "National Assembly" },
      { key: "chairmanship", label: "Chairmanship" },
      { key: "councillorship", label: "Councillorship" },
    ],
    onClick: handleFilterChange,
    className: "custom-dropdown-menu",
  };

  // More options menu (same as other components)
  const moreOptionsMenu = [
    {
      key: "1",
      label: "Item 1",
    },
    {
      key: "2",
      label: "Item 2",
    },
    {
      key: "3",
      label: "Item 3",
    },
  ];

  // Sort options (if needed)
  const sortOptions = [
    {
      key: "newest",
      label: "Newest",
    },
    {
      key: "oldest",
      label: "Oldest",
    },
    {
      key: "alphabetical",
      label: "A-Z",
    },
  ];

  // Handle adding new candidates
  const handleCandidatesAdded = (newCandidates) => {
    // Format new candidates to match the expected structure
    const formattedCandidates = newCandidates.map((candidate) => ({
      id: candidate.id,
      _id: `local-${candidate.id}`,
      fullName: candidate.name,
      position: candidate.position,
      state: candidate.state,
      avatar: candidate.avatar,
      party: candidate.party,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    }));

    setLocalCandidates((prev) => [...prev, ...formattedCandidates]);
  };

  const handleCollapseToggle = (sectionName) => {
    setOpenCollapseSection(
      openCollapseSection === sectionName ? null : sectionName
    );
  };

  return (
    <div className="w-full py-6 xl:h-full xl:flex xl:flex-col">
      <div className="hidden xl:block xl:flex-grow xl:overflow-y-auto xl:pb-16 hide-scrollbar">
        {Object.entries(groupedCandidates).map(
          ([position, candidates]) => (
            <div key={position} className="border rounded-lg mb-2">
              <CustomCollapse
                title={position}
                subtitle={`${candidates.length} candidates`}
                isShadow={false}
                isMarginBottom={false}
                isOpen={openCollapseSection === position}
                onToggle={() => handleCollapseToggle(position)}
                className="compact-collapse">
                {candidates.map((candidate) => (
                  <CandidateCard key={candidate.id} {...candidate} />
                ))}
              </CustomCollapse>
            </div>
          )
        )}
      </div>

      <div className="hidden xl:block xl:absolute xl:bottom-0 xl:left-0 xl:right-0 xl:p-4 xl:bg-white">
        <Button
          text="Onboard a New Candidate"
          icon={<PlusOutlined />}
          color="primary"
          className="w-full h-8"
          iconPosition="left"
          customBgColor="primary"
          customTextColor="white"
          onClick={handleSetupClick}
        />
      </div>

      <div className="xl:hidden">
        {/* Page Header */}
        <div>
          <PageHeader title="Candidates" />
        </div>

        <div className="flex items-center justify-between mb-6">
          <div className="flex flex-col text-left">
            <h1
              className="heading-medium text-lighterGrey"
              data-testid="total-candidates">
              {candidates.length} Total Candidates
            </h1>
            <h1 className="text-regular text-lighterGrey">
              Total number of candidates
            </h1>
          </div>
          <Button
            text="ADD NEW"
            icon={<PlusOutlined />}
            color="primary"
            className="px-2 py-1 text-small sm:px-3 sm:py-2 sm:text-small h-8 sm:h-10 min-w-[80px] sm:min-w-[100px]"
            iconPosition="left"
            customBgColor="primary"
            customTextColor="white"
            onClick={handleSetupClick}
          />
        </div>

        <SearchFilterBar
          onSearch={handleSearch}
          onRefresh={handleRefresh}
          onFilterChange={handleFilterChange}
          filterValue={filterValue}
          filterOptions={filterOptions}
          moreOptionsMenu={moreOptionsMenu}
          placeholder="Search candidates..."
          filterType="dropdown"
          sortOptions={sortOptions}
          searchValue={searchValue}
        />

        <AddCandidateModal
          isVisible={isAddCandidateModalVisible}
          onClose={() => setIsAddCandidateModalVisible(false)}
          onCandidatesAdded={handleCandidatesAdded}
        />

        {/* Candidates List - Now using filteredCandidates */}
        {filteredCandidates.length > 0 ? (
          <>
            <div className="mt-6 rounded-lg lg:border">
              {filteredCandidates.map((candidate) => (
                <CandidateCard key={candidate.id} {...candidate} />
              ))}
            </div>

            <div className="mt-6">
              <CustomPagination
                current={currentPage}
                total={filteredCandidates.length}
                pageSize={pageSize}
                onChange={handlePageChange}
                className="pb-8"
              />
            </div>
          </>
        ) : (
          <EmptyState onSetup={handleSetupClick} />
        )}
      </div>
    </div>
  );
};

export default Candidates;
